// Multi-camera director (web only, MVP). route: event/:slug/multicam
//
// Camera slots publish {stream_key}-CAM<n> to the mixer server (browser cams
// via the invite links below, app/OBS via RTMP instructions). The mixer
// re-encodes one program from the selected camera — audio always comes from
// CAM 1, so make that the main/officiant camera — and publishes it to the
// event's real stream key: the event goes live through the normal pipeline.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { View, Pressable, ActivityIndicator } from "react-native";
import tw from "twrnc";
import Hls from "hls.js";

import Text from "@/components/shared/AppText";
import AppTitle from "@/components/shared/AppTitle";
import OnboardingButton from "@/components/shared/OnboardingButton";
import EventSettingsScreenContainer from "@/components/EventSettingsScreenContainer";
import { UrlToWatch } from "@/components/shared/RtmpForm";
import useSubscribeToEventUpdates from "@/components/hooks/useSubscribeToEventUpdates";
import useWorkspaceAuth from "@/components/teams/useWorkspaceAuth";
import { makeUrl } from "@/components/globalUtils";
import AppConfig from "@/AppConfig";
import { t, tn } from "@/localization";

const SLOTS = [1, 2, 3];
const POLL_MS = 3000;

const CamTile = ({ src, ready, active, n, onCut, canCut }) => {
  const videoRef = useRef(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!ready || !video) return;
    let hls;
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.play().catch(() => {});
    } else if (Hls.isSupported()) {
      hls = new Hls({ liveDurationInfinity: true });
      hls.loadSource(src);
      hls.attachMedia(video);
    }
    return () => {
      hls?.destroy();
      if (video) video.src = "";
    };
  }, [src, ready]);

  return (
    <Pressable
      onPress={canCut && ready ? onCut : undefined}
      style={[
        tw`flex-1 rounded-lg overflow-hidden bg-black`,
        {
          minWidth: 180,
          borderWidth: 3,
          borderColor: active ? "#F84123" : "transparent",
        },
      ]}
    >
      {ready ? (
        <video
          ref={videoRef}
          muted
          playsInline
          autoPlay
          style={{
            width: "100%",
            aspectRatio: "16/9",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <View
          style={[
            tw`w-full items-center justify-center`,
            { aspectRatio: 16 / 9 },
          ]}
        >
          <Text style={tw`text-gray-400 text-xs`}>
            {t("Waiting for camera…")}
          </Text>
        </View>
      )}
      <View
        style={[
          tw`absolute top-1 left-1 px-1.5 py-0.5 rounded`,
          { backgroundColor: "rgba(0,0,0,0.6)" },
        ]}
      >
        <Text style={tw`text-white text-xs font-semibold`}>
          {active
            ? t("CAM {number} · ON AIR", { number: n })
            : t("CAM {number}", { number: n })}
        </Text>
      </View>
    </Pressable>
  );
};

const MultiCam = ({ route }) => {
  const { slug } = route?.params || {};
  // Teams: the account the event is in (yours, or the one you work in).
  const { broadcaster, custom_domain } = useWorkspaceAuth();
  const { event, loading } = useSubscribeToEventUpdates(
    broadcaster && slug ? { broadcaster, slug } : null,
    Boolean(broadcaster && slug)
  );
  const streamKey = event?.stream_key;

  const [status, setStatus] = useState(null); // { slots, mixing, activeInput }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const api = useCallback(
    async (path, method = "GET") => {
      const res = await fetch(
        `${AppConfig.MIXER_CONTROL_BASE}/v1/mix/${streamKey}/${path}`,
        { method }
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || t("mixer error"));
      return body;
    },
    [streamKey]
  );

  useEffect(() => {
    if (!streamKey) return;
    let stop = false;
    const poll = async () => {
      try {
        const s = await api("status");
        if (!stop) {
          setStatus(s);
          setError(null);
        }
      } catch (e) {
        if (!stop)
          setError(
            e.message === "Failed to fetch"
              ? t("Mixer server unreachable")
              : e.message
          );
      }
    };
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => {
      stop = true;
      clearInterval(timer);
    };
  }, [streamKey, api]);

  // Mixer inputs are CAM1..N contiguous; CAM1 carries the program audio.
  const readySlots = SLOTS.filter((n) => status?.slots?.[n]);
  let contiguous = 0;
  for (const n of SLOTS) {
    if (status?.slots?.[n] && n === contiguous + 1) contiguous = n;
    else break;
  }
  const canStart = !status?.mixing && contiguous >= 2;

  const startMix = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`start?slots=${contiguous}`, "POST");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const stopMix = async () => {
    if (!window.confirm(t("End the multi-camera stream?"))) return;
    setBusy(true);
    try {
      await api("stop", "POST");
    } finally {
      setBusy(false);
    }
  };
  const cutTo = async (n) => {
    try {
      setStatus((s) => (s ? { ...s, activeInput: n } : s)); // optimistic
      await api(`cut/${n}`, "POST");
    } catch (e) {
      setError(e.message);
    }
  };

  const watchUrl = event ? makeUrl(event, custom_domain) : "";
  const isLive = event?.event_status === "live";

  return (
    <EventSettingsScreenContainer loading={loading && !event}>
      <View style={tw`w-full max-w-[860px] self-center px-4 pb-10`}>
        <AppTitle
          text={t("{title} — Multi-Camera", { title: event?.title || "" })}
          style={tw`text-left mb-1`}
        />
        <Text style={tw`text-gray-500 mb-3`}>
          {t(
            "Camera 1 is the main camera — its audio is what viewers hear. Open each link on a phone, laptop or camera setup, then start the stream once at least cameras 1 and 2 are in."
          )}
        </Text>

        {/* Program status */}
        <View style={tw`flex-row items-center mb-3`}>
          <View
            style={[
              tw`w-2.5 h-2.5 rounded-full mr-2`,
              {
                backgroundColor:
                  status?.mixing && isLive
                    ? "#4ade80"
                    : status?.mixing
                    ? "#fbbf24"
                    : "#999",
              },
            ]}
          />
          <Text style={tw`font-semibold`}>
            {status?.mixing
              ? isLive
                ? t("LIVE")
                : t("Starting…")
              : t("Not streaming")}
          </Text>
          {isLive && (
            <Text style={tw`ml-3 text-sm text-gray-500`}>
              {tn(
                "{count} watching",
                "{count} watching",
                event?.liveViewers ?? 0
              )}
            </Text>
          )}
        </View>
        {error && <Text style={tw`text-red-600 mb-2`}>{error}</Text>}

        {/* Camera tiles — click to cut while mixing */}
        <View style={[tw`flex-row`, { gap: 10 }]}>
          {SLOTS.map((n) => (
            <CamTile
              key={n}
              n={n}
              ready={Boolean(status?.slots?.[n])}
              active={status?.mixing && status?.activeInput === n}
              canCut={Boolean(status?.mixing)}
              onCut={() => cutTo(n)}
              src={`${AppConfig.MIXER_HLS_BASE}/cam/${streamKey}-CAM${n}/index.m3u8`}
            />
          ))}
        </View>
        {status?.mixing && (
          <Text style={tw`text-xs text-gray-400 mt-1`}>
            {t("Click a camera to cut the program to it.")}
          </Text>
        )}

        {/* Controls */}
        <View style={tw`mt-4`}>
          {!status?.mixing ? (
            <OnboardingButton
              text={
                canStart
                  ? t("Start Live Stream ({count} cameras)", {
                      count: contiguous,
                    })
                  : t("Waiting for cameras 1 and 2…")
              }
              icon="videocam-outline"
              disabled={!canStart}
              loading={busy}
              onPress={startMix}
            />
          ) : (
            <OnboardingButton
              text={t("End Stream")}
              icon="stop-circle-outline"
              loading={busy}
              onPress={stopMix}
            />
          )}
        </View>

        {/* Invite links */}
        <View style={tw`mt-6`}>
          {streamKey ? (
            SLOTS.map((n) => (
              <UrlToWatch
                key={n}
                help={null}
                title={
                  n === 1
                    ? t("Camera {number} invite link (main camera + audio)", {
                        number: n,
                      })
                    : t("Camera {number} invite link", { number: n })
                }
                url={`${
                  typeof window !== "undefined"
                    ? window.location.origin
                    : "https://broadcaster.eventlive.pro"
                }/stream/${streamKey}-CAM${n}`}
              />
            ))
          ) : (
            <ActivityIndicator />
          )}
          <UrlToWatch title={t("Link to Watch")} url={watchUrl} />
        </View>
      </View>
    </EventSettingsScreenContainer>
  );
};

export default MultiCam;
